/* eslint-disable complexity */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";
import { PageType } from "../constants";
import { CrmGameConceptStatus, CurrentGameConcept } from "../models/crmStatus";
import {
  AaaGameInformationAvailabilityDataType,
  AaaGameInformationConceptDetailsDataType,
  AaaGameInformationConfigurationsDataType,
  AaaGameInformationDistributionInformationDataType,
  AaaGameInformationGameExpectationsDataType,
  AaaGameInformationSupplementaryDataType,
  AaaGameInformationTechnicalCapabilitiesDataType,
  AaaGameInformationUgcDataType,
  AaaGameInformationXboxNetworkDataType,
} from "../models/forms";
import { Programs } from "../models/programsConfig";
import { AaaApplicationStep, AaaApplicationStepsOrder } from "../models/status";
import {
  AaaGameInformationFormId,
  AaaGameInformationSubFormsId,
} from "../subForms/aaaGameInformation/AaaGameInformationPageConstants";
import { statusActions } from "../redux/actions/statusActions";
import { OnboardingRootState } from "../redux/reducers/rootReducer";
import {
  getAaaApplicationStep,
  getCurrentGameConcept,
  getIsInitialCrmFetchDone,
  getSubFormData,
} from "../redux/selectors";
import {
  isAaaGameInformationAvailabilityFormValid,
  isAaaGameInformationConfigurationsFormValid,
  isAaaGameInformationDetailsFormValid,
  isAaaGameInformationDistributionInformationFormValid,
  isAaaGameInformationGameExpectationsValid,
  isAaaGameInformationSupplementaryDataFormValid,
  isAaaGameInformationTechnicalCapabilitiesFormValid,
  isAaaGameInformationUgcFormValid,
  isAaaGameInformationXboxNetworkFormValid,
} from "./formValidation";
import { compareCrmGameConceptStatus } from "./idXboxStatusHelpers";

export const compareAaaApplicationStepStatus = (
  step1: AaaApplicationStep,
  step2: AaaApplicationStep
) => {
  const indexStep1 = AaaApplicationStepsOrder.indexOf(step1);
  const indexStep2 = AaaApplicationStepsOrder.indexOf(step2);
  return indexStep1 < indexStep2 ? -1 : indexStep1 > indexStep2 ? 1 : 0;
};

export const checkAaaGameInformationStep = async (
  step: AaaApplicationStep,
  crmGameConceptStatus: CrmGameConceptStatus | undefined,
  gameInfoConceptSubFormData: AaaGameInformationConceptDetailsDataType,
  gameInfoDistributionInformationSubFormData: AaaGameInformationDistributionInformationDataType,
  gameInfoGameExpectationsSubFormData: AaaGameInformationGameExpectationsDataType,
  gameInfoAvailabilitiesSubFormData: AaaGameInformationAvailabilityDataType,
  gameInfoTechnicalCapabilitiesSubFormData: AaaGameInformationTechnicalCapabilitiesDataType,
  gameInfoConfigurationsSubFormData: AaaGameInformationConfigurationsDataType,
  gameInfoXboxNetworkSubFormData: AaaGameInformationXboxNetworkDataType,
  gameInfoUgcSubFormData: AaaGameInformationUgcDataType,
  gameInfoSupplementaryDataSubFormData: AaaGameInformationSupplementaryDataType
): Promise<AaaApplicationStep> => {
  if (
    step !== AaaApplicationStep.GameInformation ||
    !gameInfoConceptSubFormData ||
    !gameInfoDistributionInformationSubFormData ||
    !gameInfoGameExpectationsSubFormData ||
    !gameInfoAvailabilitiesSubFormData ||
    !gameInfoTechnicalCapabilitiesSubFormData ||
    !gameInfoConfigurationsSubFormData ||
    !gameInfoXboxNetworkSubFormData ||
    !gameInfoUgcSubFormData ||
    !gameInfoSupplementaryDataSubFormData
  ) {
    return step;
  }

  if (
    crmGameConceptStatus !== CrmGameConceptStatus.Unset &&
    crmGameConceptStatus !== CrmGameConceptStatus.MoreInfoNeeded &&
    crmGameConceptStatus !== undefined
  ) {
    return AaaApplicationStep.ApplicationReadyForReview;
  }

  const isGameInfoDetailsFormValid = await isAaaGameInformationDetailsFormValid(
    gameInfoConceptSubFormData
  );
  const isGameInfoDistributionInformationFormValid =
    await isAaaGameInformationDistributionInformationFormValid(
      gameInfoDistributionInformationSubFormData
    );
  const isGameInfoGameExpectationsFormValid = await isAaaGameInformationGameExpectationsValid(
    gameInfoGameExpectationsSubFormData
  );
  const isGameInfoPricingFormValid = await isAaaGameInformationAvailabilityFormValid(
    gameInfoAvailabilitiesSubFormData
  );
  const isGameInfoTechnicalCapabilitiesFormValid =
    await isAaaGameInformationTechnicalCapabilitiesFormValid(
      gameInfoTechnicalCapabilitiesSubFormData
    );
  const isGameInfoConfigurationsFormValid = await isAaaGameInformationConfigurationsFormValid(
    gameInfoConfigurationsSubFormData
  );
  const isGameInfoXboxNetworkFormValid = await isAaaGameInformationXboxNetworkFormValid(
    gameInfoXboxNetworkSubFormData
  );
  const isGameInfoUgcFormValid = await isAaaGameInformationUgcFormValid(gameInfoUgcSubFormData);
  const isGameInfoSupplementaryDataFormValid = await isAaaGameInformationSupplementaryDataFormValid(
    gameInfoSupplementaryDataSubFormData
  );

  return isGameInfoDetailsFormValid &&
    isGameInfoDistributionInformationFormValid &&
    isGameInfoGameExpectationsFormValid &&
    isGameInfoPricingFormValid &&
    isGameInfoTechnicalCapabilitiesFormValid &&
    isGameInfoConfigurationsFormValid &&
    isGameInfoXboxNetworkFormValid &&
    isGameInfoUgcFormValid &&
    isGameInfoSupplementaryDataFormValid
    ? AaaApplicationStep.ApplicationReadyForReview
    : step;
};

export const calculateAaaApplicationStep = async (
  crmGameConceptStatus: CrmGameConceptStatus | undefined,
  gameInfoConceptSubFormData: AaaGameInformationConceptDetailsDataType,
  gameInfoDistributionInformationSubFormData: AaaGameInformationDistributionInformationDataType,
  gameInfoGameExpectationsSubFormData: AaaGameInformationGameExpectationsDataType,
  gameInfoPricingAndAvailabilitiesSubFormData: AaaGameInformationAvailabilityDataType,
  gameInfoTechnicalCapabilitiesSubFormData: AaaGameInformationTechnicalCapabilitiesDataType,
  gameInfoConfigurationsSubFormData: AaaGameInformationConfigurationsDataType,
  gameInfoXboxNetworkSubFormData: AaaGameInformationXboxNetworkDataType,
  gameInfoUgcSubFormData: AaaGameInformationUgcDataType,
  gameInfoSupplementaryDataSubFormData: AaaGameInformationSupplementaryDataType
) => {
  /** ********************************** Steps logic ************************************/
  // 0: Application rejected
  if (crmGameConceptStatus === CrmGameConceptStatus.Rejected) {
    return AaaApplicationStep.ApplicationRejected;
  }

  // Game information
  let step = AaaApplicationStep.GameInformation;

  // Game information -> Application ready for review
  step = await checkAaaGameInformationStep(
    step,
    crmGameConceptStatus,
    gameInfoConceptSubFormData,
    gameInfoDistributionInformationSubFormData,
    gameInfoGameExpectationsSubFormData,
    gameInfoPricingAndAvailabilitiesSubFormData,
    gameInfoTechnicalCapabilitiesSubFormData,
    gameInfoConfigurationsSubFormData,
    gameInfoXboxNetworkSubFormData,
    gameInfoUgcSubFormData,
    gameInfoSupplementaryDataSubFormData
  );

  // Application ready for review -> Application being reviewed
  step = checkApplicationReadyForReviewStep(step, crmGameConceptStatus);

  // Application being reviewed -> Application approved / denied
  return checkApplicationBeingReviewedStep(step, crmGameConceptStatus);
};

const checkApplicationReadyForReviewStep = (
  step: AaaApplicationStep,
  currentGameConceptStatus: CrmGameConceptStatus | undefined
): AaaApplicationStep => {
  if (step !== AaaApplicationStep.ApplicationReadyForReview || !currentGameConceptStatus) {
    return step;
  }

  const applicationBeingReviewedComparison = compareCrmGameConceptStatus(
    currentGameConceptStatus,
    CrmGameConceptStatus.UnderReview
  );

  return applicationBeingReviewedComparison >= 0
    ? AaaApplicationStep.ApplicationIsBeingReviewed
    : step;
};

const checkApplicationBeingReviewedStep = (
  step: AaaApplicationStep,
  currentGameConceptStatus: CrmGameConceptStatus | undefined
): AaaApplicationStep => {
  if (step !== AaaApplicationStep.ApplicationIsBeingReviewed || !currentGameConceptStatus) {
    return step;
  }

  switch (currentGameConceptStatus) {
    case CrmGameConceptStatus.Rejected:
    case CrmGameConceptStatus.Accepted:
      return AaaApplicationStep.ApplicationComplete;
    default:
      return step;
  }
};

export const useCalculateAaaStatus = async (pageType: PageType | undefined) => {
  const dispatch = useDispatch();

  const isSignedIn: boolean = useSelector(getIsUserSignedIn);
  const currentStep: AaaApplicationStep | undefined = useSelector(getAaaApplicationStep);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);

  // AAA Game Information sub form data
  const gameInfoConceptSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.ConceptDetails)
  ) as AaaGameInformationConceptDetailsDataType;
  const gameInfoDistributionInformationSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(
      state,
      AaaGameInformationFormId,
      AaaGameInformationSubFormsId.DistributionInformation
    )
  ) as AaaGameInformationDistributionInformationDataType;
  const gameInfoGameExpectationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.GameExpectations)
  ) as AaaGameInformationGameExpectationsDataType;
  const gameInfoAvailabilitiesSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.Availability)
  ) as AaaGameInformationAvailabilityDataType;
  const gameInfoTechnicalCapabilitiesSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(
      state,
      AaaGameInformationFormId,
      AaaGameInformationSubFormsId.TechnicalCapabilities
    )
  ) as AaaGameInformationTechnicalCapabilitiesDataType;
  const gameInfoConfigurationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.Configurations)
  ) as AaaGameInformationConfigurationsDataType;
  const gameInfoXboxNetworkSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.XboxNetwork)
  ) as AaaGameInformationXboxNetworkDataType;
  const gameInfoUgcSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.Ugc)
  ) as AaaGameInformationUgcDataType;
  const gameInfoSupplementaryDataSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, AaaGameInformationFormId, AaaGameInformationSubFormsId.SupplementaryData)
  ) as AaaGameInformationSupplementaryDataType;

  const currentGameConcept: CurrentGameConcept | undefined = useSelector(getCurrentGameConcept);

  useEffect(() => {
    const calculateNextStep = async () => {
      // 0: Must be signed in
      if (!isSignedIn || pageType !== PageType.AaaApplication || !isInitialCrmFetchDone) {
        return;
      }

      const step = await calculateAaaApplicationStep(
        currentGameConcept?.crmGameConceptStatus,
        gameInfoConceptSubFormData,
        gameInfoDistributionInformationSubFormData,
        gameInfoGameExpectationsSubFormData,
        gameInfoAvailabilitiesSubFormData,
        gameInfoTechnicalCapabilitiesSubFormData,
        gameInfoConfigurationsSubFormData,
        gameInfoXboxNetworkSubFormData,
        gameInfoUgcSubFormData,
        gameInfoSupplementaryDataSubFormData
      );

      if (step !== currentStep) {
        dispatch(statusActions.updateStep(step, Programs.Aaa));
      }
    };

    calculateNextStep();
  }, [
    dispatch,
    isSignedIn,
    currentStep,
    gameInfoConceptSubFormData,
    gameInfoGameExpectationsSubFormData,
    gameInfoConfigurationsSubFormData,
    gameInfoAvailabilitiesSubFormData,
    gameInfoTechnicalCapabilitiesSubFormData,
    gameInfoXboxNetworkSubFormData,
    currentGameConcept,
    pageType,
    isInitialCrmFetchDone,
    gameInfoDistributionInformationSubFormData,
    gameInfoUgcSubFormData,
    gameInfoSupplementaryDataSubFormData,
  ]);
};
